import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  BookOpenCheck,
  Check,
  ChevronRight,
  Clock3,
  Download,
  FileText,
  FolderPlus,
  History,
  Loader2,
  Sparkles,
  Upload,
} from 'lucide-react';
import api from '../../utils/api';
import { writtenEpisodeCount } from './projectEpisodeCount';
import { useTeamBrand } from '../branding/TeamBrandContext';
import { printReviewReport } from './printReviewReport';

const PREVIEW_HISTORY = [
  {
    id: 'preview-review-1',
    script_title: '雾港来信',
    rating: 'A',
    score: 86,
    conclusion: '值得继续打磨',
    created_at: '2026-07-31T10:42:00.000Z',
  },
  {
    id: 'preview-review-2',
    script_title: '老板的失忆合约',
    rating: 'B',
    score: 74,
    conclusion: '钩子需要加强',
    created_at: '2026-07-28T15:16:00.000Z',
  },
];

const PREVIEW_REPORT = `## 一句话判断
这个故事的悬念入口清楚，人物关系也有继续追下去的动力；目前最值得补的是前三集的即时回报。

### 做得好的地方
- 雾港、旧信和失踪姐姐形成了统一的视觉记忆。
- 主角目标明确，第一集就能建立追查方向。
- 周屿既像帮助者又像隐瞒者，关系自带张力。

### 优先修改
- 第 1 集结尾增加一个能被观众立即验证的新线索，不只停在“发现异常”。
- 第 2 集让主角主动做一次选择，并为这个选择付出代价。
- 删掉重复解释姐姐失踪背景的台词，把信息交给动作和物件。

### 制作建议
保留潮湿、克制的悬疑气质；人物近景要比空镜更早出现，让观众先跟住人，再跟住谜。`;

const MODE_OPTIONS = [
  { id: 'project', label: '平台剧本', icon: BookOpenCheck, description: '直接检查已经在小影里创作的故事' },
  { id: 'text', label: '粘贴文本', icon: FileText, description: '把正在修改的剧本贴进来，马上开检' },
  { id: 'upload', label: '上传文件', icon: Upload, description: '支持 PDF、DOCX、DOC 和 TXT' },
];

function ratingTone(rating) {
  return ['S', 'A', 'B', 'C'].includes(rating) ? rating.toLowerCase() : 'b';
}

function ReviewReport({ result, title }) {
  const brand = useTeamBrand();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportNotice, setExportNotice] = useState('');

  useEffect(() => {
    setExportError('');
    setExportNotice('');
  }, [result]);

  if (!result) return null;
  const meta = result.meta || result;
  const report = result.report || '';
  const lines = report.split('\n');

  const exportReport = async () => {
    if (exporting) return;
    setExporting(true);
    setExportError('');
    setExportNotice('');
    try {
      await printReviewReport({ result, title, brandName: brand.name });
      setExportNotice('报告已打开，可在打印窗口中选择“另存为 PDF”。');
    } catch (error) {
      setExportError(getErrorMessage(error, '报告导出失败，请重试。'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="writing-review-result">
      <header>
        <div className={`writing-review-grade is-${ratingTone(meta.rating)}`}>
          <strong>{meta.rating || 'B'}</strong>
          <span>{meta.score ? `${meta.score} 分` : '审核完成'}</span>
        </div>
        <div>
          <span>REVIEW REPORT</span>
          <h3>{title || '剧本审核报告'}</h3>
          <p>{meta.conclusion || '已经读完，下面是最值得先改的地方。'}</p>
        </div>
        <button type="button" onClick={exportReport} disabled={exporting} aria-busy={exporting}>
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
          {exporting ? '正在准备报告…' : '导出报告'}
        </button>
      </header>
      {exportError && <div className="writing-review-export-feedback writing-review-error" role="alert"><AlertCircle size={16} />{exportError}</div>}
      {exportNotice && <p className="writing-review-export-feedback" role="status">{exportNotice}</p>}
      <div className="writing-review-report-body">
        {lines.map((line, index) => {
          const key = `${index}-${line.slice(0, 12)}`;
          if (line.startsWith('### ')) return <h4 key={key}>{line.slice(4)}</h4>;
          if (line.startsWith('## ')) return <h3 key={key}>{line.slice(3)}</h3>;
          if (line.startsWith('- ')) return <p className="is-bullet" key={key}><span>•</span>{line.slice(2)}</p>;
          if (!line.trim()) return <div className="writing-review-report-gap" key={key} />;
          return <p key={key}>{line.replace(/\*\*/g, '')}</p>;
        })}
      </div>
    </div>
  );
}

export default function WritingReviewPanel({
  projects = [],
  preview = false,
  initialProjectId = '',
  initialMode = 'project',
  onProjectCreated,
}) {
  const brand = useTeamBrand();
  const fileRef = useRef(null);
  const [mode, setMode] = useState(MODE_OPTIONS.some(option => option.id === initialMode) ? initialMode : 'project');
  const [projectId, setProjectId] = useState(initialProjectId || projects[0]?.id || '');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [history, setHistory] = useState(preview ? PREVIEW_HISTORY : []);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(!preview);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [reviewedText, setReviewedText] = useState('');

  useEffect(() => {
    setMode(MODE_OPTIONS.some(option => option.id === initialMode) ? initialMode : 'project');
    setResult(null);
    setError('');
    setSaved(false);
    setReviewedText('');
  }, [initialMode]);

  useEffect(() => {
    if (initialProjectId) setProjectId(initialProjectId);
    else if (!projectId && projects[0]?.id) setProjectId(projects[0].id);
  }, [initialProjectId, projectId, projects]);

  const selectedProject = useMemo(
    () => projects.find(project => String(project.id) === String(projectId)),
    [projectId, projects],
  );

  const loadHistory = async () => {
    if (preview) return;
    setHistoryLoading(true);
    try {
      const response = await api.get('/review/history');
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const resetOutput = nextMode => {
    setMode(nextMode);
    setResult(null);
    setError('');
    setSaved(false);
    setReviewedText('');
  };

  const submit = async () => {
    setError('');
    setResult(null);
    setSaved(false);

    if (mode === 'project' && !projectId) return setError(getErrorMessage(brand.text('先选一部剧本，小影才知道该读哪一本。')));
    if (mode === 'text' && !text.trim()) return setError('还没有看到剧本内容，先贴一点进来吧。');
    if (mode === 'upload' && !file) return setError('先选一个剧本文件，再开始审核。');

    setLoading(true);
    try {
      if (preview) {
        const previewTitle = mode === 'project' ? selectedProject?.title : title || file?.name?.replace(/\.[^.]+$/, '') || '未命名剧本';
        setTitle(previewTitle || '未命名剧本');
        setReviewedText(mode === 'text' ? text : '');
        setResult({ report: PREVIEW_REPORT, meta: { rating: 'A', score: 86, conclusion: '值得继续打磨' } });
        setHistory(current => [{
          id: `preview-review-${Date.now()}`,
          script_title: previewTitle,
          rating: 'A',
          score: 86,
          conclusion: '值得继续打磨',
          created_at: new Date().toISOString(),
        }, ...current]);
        return;
      }

      let response;
      if (mode === 'project') {
        response = await api.post(`/review/agent-project/${projectId}`);
        setTitle(selectedProject?.title || '平台剧本');
      } else if (mode === 'upload') {
        const form = new FormData();
        form.append('file', file);
        form.append('title', title || file.name.replace(/\.[^.]+$/, ''));
        response = await api.post('/review/upload', form);
        setReviewedText(response.data?.extracted_text || '');
      } else {
        response = await api.post('/review/text', { title: title || '未命名剧本', text });
        setReviewedText(text);
      }
      setResult(response.data);
      await loadHistory();
    } catch (requestError) {
      setError(getErrorMessage(requestError, '这次没有读完，可以稍后再试一次。'));
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async item => {
    setTitle(item.script_title || item.file_name || '剧本审核报告');
    setSaved(false);
    setReviewedText('');
    if (preview) {
      setResult({ report: PREVIEW_REPORT, meta: item });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/review/result/${item.id}`);
      setResult({ report: response.data.report, meta: response.data });
    } catch (requestError) {
      setError(getErrorMessage(requestError, '这份历史报告暂时没有打开。'));
    } finally {
      setLoading(false);
    }
  };

  const saveAsProject = async () => {
    if (!reviewedText || saving || saved) return;
    setSaving(true);
    setError('');
    try {
      if (preview) {
        setSaved(true);
        return;
      }
      const created = await api.post('/agent/projects', { title: title || '审核导入剧本' });
      await api.put(`/agent/projects/${created.data.id}/episodes/1`, {
        title: '导入稿',
        content: reviewedText,
        summary: '由剧本审核导入',
      });
      setSaved(true);
      onProjectCreated?.();
    } catch (requestError) {
      setError(getErrorMessage(requestError, '审核完成了，但保存成新剧本没有成功。'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="writing-review-workspace" aria-labelledby="writing-review-title">
      <header className="writing-review-heading">
        <div>
          <span>STORY CHECK</span>
          <h2 id="writing-review-title">先让故事过一遍片场体检</h2>
          <p>{brand.text('不是判卷子。小影会告诉你哪里抓人、哪里松了，以及下一刀该剪在哪里。')}</p>
        </div>
        <button type="button" onClick={() => setHistoryOpen(current => !current)} aria-expanded={historyOpen}>
          <History size={16} />审核记录 <span>{history.length}</span>
        </button>
      </header>

      {preview && <p className="mb-4 text-[12px] leading-6 text-stone-500 dark:text-white/55">审核预览：展示示例报告，不会审核所选文件、保存作品或消耗点数。</p>}

      <div className={`writing-review-layout${historyOpen ? '' : ' is-history-closed'}`}>
        <div className={`writing-review-input-card is-${mode}`}>
          <div className="writing-review-modes" role="tablist" aria-label="选择审核方式">
            {MODE_OPTIONS.map(option => {
              const Icon = option.icon;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === option.id}
                  className={mode === option.id ? 'is-selected' : ''}
                  onClick={() => resetOutput(option.id)}
                  key={option.id}
                >
                  <Icon size={16} />
                  <span><strong>{option.label}</strong><small>{brand.text(option.description)}</small></span>
                </button>
              );
            })}
          </div>

          <div className="writing-review-form">
            {mode === 'project' && (
              <label>
                <span>选择要检查的剧本</span>
                <select value={projectId} onChange={event => setProjectId(event.target.value)}>
                  <option value="">选择一部剧本</option>
                  {projects.map(project => (
                    <option value={project.id} key={project.id}>{project.title}</option>
                  ))}
                </select>
                {selectedProject && <small>{writtenEpisodeCount(selectedProject)} 集 · {selectedProject.genre || '题材待定'} · {stageForReview(selectedProject.current_stage)}</small>}
              </label>
            )}

            {mode !== 'project' && (
              <label>
                <span>剧本标题</span>
                <input value={title} onChange={event => setTitle(event.target.value)} placeholder="给这份稿子起个名字（选填）" />
              </label>
            )}

            {mode === 'text' && (
              <label>
                <span>剧本内容</span>
                <textarea value={text} onChange={event => setText(event.target.value)} rows={13} placeholder="把完整剧本、分集大纲或正在打磨的一集贴在这里……" />
                <small>{text.length.toLocaleString('zh-CN')} 字符</small>
              </label>
            )}

            {mode === 'upload' && (
              <button type="button" className={`writing-review-upload${file ? ' has-file' : ''}`} onClick={() => fileRef.current?.click()}>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={event => {
                    const nextFile = event.target.files?.[0] || null;
                    setFile(nextFile);
                    if (nextFile && !title) setTitle(nextFile.name.replace(/\.[^.]+$/, ''));
                  }}
                  tabIndex={-1}
                />
                {file ? <><Check size={21} /><strong>{file.name}</strong><small>{Math.max(1, Math.round(file.size / 1024))} KB · 点击更换</small></> : <><Upload size={22} /><strong>选择剧本文件</strong><small>PDF、DOCX、DOC、TXT · 最大 20MB</small></>}
              </button>
            )}

            {error && <div className="writing-review-error" role="alert"><AlertCircle size={16} />{error}</div>}

            <footer>
              <span><Sparkles size={14} />从钩子、人物、冲突、节奏和可拍性一起检查</span>
              <button type="button" onClick={submit} disabled={loading}>
                {loading ? <><Loader2 size={16} className="animate-spin" />正在读剧本</> : <>开始审核<ChevronRight size={16} /></>}
              </button>
            </footer>
          </div>
        </div>

        <aside className="writing-review-history" aria-label="审核记录">
          <header><span>最近的体检单</span><small>最多保留 30 条</small></header>
          {historyLoading ? (
            <div className="writing-review-history-empty"><Loader2 size={18} className="animate-spin" />正在整理记录</div>
          ) : history.length ? history.map(item => (
            <button type="button" onClick={() => openHistory(item)} key={item.id}>
              <span className={`is-${ratingTone(item.rating)}`}>{item.rating || '—'}</span>
              <div><strong>{item.script_title || item.file_name || '未命名剧本'}</strong><small><Clock3 size={12} />{new Date(item.created_at).toLocaleDateString('zh-CN')} · {item.conclusion || '审核完成'}</small></div>
              <em>{item.score ? `${item.score} 分` : ''}</em>
            </button>
          )) : <div className="writing-review-history-empty"><History size={19} />第一次审核会从这里留下记录</div>}
        </aside>
      </div>

      {loading && !result ? (
        <div className="writing-review-reading"><span><Loader2 size={22} /></span><h3>{brand.text('小影正在精读这份稿子')}</h3><p>先找故事真正抓人的地方，再告诉你哪里值得动刀。</p></div>
      ) : result && (
        <>
          {reviewedText && (
            <div className="writing-review-save-strip">
              <div><FolderPlus size={17} /><span><strong>{preview ? '示例报告' : '这份外部稿子还没进入剧本项目'}</strong><small>{preview ? '可体验保存反馈，本次不会创建剧本。' : '保存后会作为一部新剧本出现在“剧本项目”里。'}</small></span></div>
              <button type="button" onClick={saveAsProject} disabled={saving || saved}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : saved ? <Check size={15} /> : <FolderPlus size={15} />}
                {preview ? (saved ? '保存演示完成' : '体验保存') : (saved ? '已经保存' : '保存为新剧本')}
              </button>
            </div>
          )}
          <ReviewReport result={result} title={title || selectedProject?.title} />
        </>
      )}
    </section>
  );
}

function stageForReview(stage) {
  return ({ bible: '构思中', outline: '搭故事', writing: '写正文', script: '写正文', review: '打磨中' })[stage] || '创作中';
}
